import React, { useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Rating,
    Paper,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    LinearProgress,
    Stepper,
    Step,
    StepLabel
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    School as SchoolIcon,
    Group as GroupIcon,
    Person as PersonIcon,
    Psychology as PsychologyIcon,
    EmojiEvents as TrophyIcon,
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    Flag as FlagIcon
} from '@mui/icons-material';
import {
    Timeline,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
    TimelineDot,
    TimelineConnector,
    TimelineContent
} from '@mui/lab';

const HolisticAssessment = () => {
    const [assessments, setAssessments] = useState([
        {
            id: 1,
            studentName: 'Aarav Sharma',
            class: 'Class 10',
            section: 'A',
            assessmentDate: '2025-11-01',
            assessedBy: 'Mrs. Sunita Reddy',
            academicPerformance: 4.5,
            extracurricular: 4.0,
            socialSkills: 4.5,
            leadership: 4.0,
            creativity: 4.5,
            technicalSkills: 5.0,
            overallScore: 4.4,
            strengths: ['Problem Solving', 'Innovation', 'Team Work'],
            developmentAreas: ['Public Speaking', 'Time Management'],
            teacherFeedback: 'Exceptional student with strong analytical skills.',
            parentFeedback: 'Very dedicated and focused on learning.',
            peerFeedback: 'Great team player and always helps others.'
        }
    ]);

    const [competitionProgress, setCompetitionProgress] = useState([
        {
            stages: [
                { name: 'Registration', status: 'completed', date: '2025-10-05', score: '-' },
                { name: 'Stage 1 - MAT & SAT', status: 'completed', date: '2025-11-03', score: '168/200' },
                { name: 'Stage 2 - National', status: 'qualified', date: '2025-12-15', score: 'Qualified' }
            ],
            currentStage: 2,
            achievements: ['State Qualifier'],
            preparationProgress: 90
        }
    ]);

    const competencies = [
        { name: 'Academic Performance', key: 'academicPerformance', color: '#3f51b5' },
        { name: 'Extracurricular Activities', key: 'extracurricular', color: '#ff9800' },
        { name: 'Social & Emotional Skills', key: 'socialSkills', color: '#4caf50' },
        { name: 'Leadership Qualities', key: 'leadership', color: '#e91e63' },
        { name: 'Creative Thinking', key: 'creativity', color: '#9c27b0' },
        { name: 'Technical Skills', key: 'technicalSkills', color: '#00bcd4' }
    ];

    const getCompetencyColor = (score) => {
        if (score >= 4.5) return '#4caf50';
        if (score >= 3.5) return '#2196f3';
        if (score >= 2.5) return '#ff9800';
        return '#f44336';
    };

    const getStageColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'in-progress': return 'primary';
            case 'qualified': return 'success';
            case 'pending': return 'default';
            default: return 'default';
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                    NEP 2020 Compliant Holistic Assessment
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />}>
                    New Assessment
                </Button>
            </Box>

            <Grid container spacing={3}>
                {assessments.map((assessment) => (
                    <Grid item xs={12} key={assessment.id}>
                        <Card>
                            <CardContent>
                                {/* Student Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#3f51b5', width: 56, height: 56 }}>
                                            {assessment.studentName.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" fontWeight={600}>
                                                {assessment.studentName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {assessment.class} / {assessment.section}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Assessed by: {assessment.assessedBy} | Date: {new Date(assessment.assessmentDate).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h3" fontWeight={700} color="primary">
                                            {assessment.overallScore}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Overall Score
                                        </Typography>
                                        <Rating value={assessment.overallScore} precision={0.1} readOnly size="small" />
                                    </Box>
                                </Box>

                                {/* Competency Scores */}
                                <Paper sx={{ p: 3, mb: 3, bgcolor: '#f5f5f5' }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                                        Competency Assessment (NEP 2020 Framework)
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {competencies.map((competency) => (
                                            <Grid item xs={12} sm={6} key={competency.key}>
                                                <Box sx={{ mb: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {competency.name}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600} color={getCompetencyColor(assessment[competency.key])}>
                                                            {assessment[competency.key]}/5.0
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={(assessment[competency.key] / 5) * 100}
                                                        sx={{
                                                            height: 8,
                                                            borderRadius: 4,
                                                            bgcolor: '#e0e0e0',
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor: getCompetencyColor(assessment[competency.key])
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Paper>

                                {/* Strengths and Development Areas */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2, bgcolor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                                Key Strengths
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {assessment.strengths.map((strength, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={strength}
                                                        color="success"
                                                        size="small"
                                                    />
                                                ))}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                                Development Areas
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {assessment.developmentAreas.map((area, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={area}
                                                        color="warning"
                                                        size="small"
                                                    />
                                                ))}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                {/* 360-Degree Feedback */}
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            360-Degree Feedback
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <SchoolIcon color="primary" />
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            Teacher Feedback
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2">
                                                        {assessment.teacherFeedback}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Paper sx={{ p: 2, bgcolor: '#f3e5f5' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <PersonIcon color="secondary" />
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            Parent Feedback
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2">
                                                        {assessment.parentFeedback}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <GroupIcon color="success" />
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            Peer Feedback
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2">
                                                        {assessment.peerFeedback}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Competition Progress Tracking Section */}
            <Box sx={{ mt: 5 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                    Competition Progress Tracking
                </Typography>

                <Grid container spacing={3}>
                    {competitionProgress.map((competition, idx) => (
                        <Grid item xs={12} key={idx}>
                            <Card>
                                <CardContent>
                                    {/* Student Header */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#3f51b5', width: 56, height: 56 }}>
                                                {assessments[0].studentName.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6" fontWeight={600}>
                                                    {assessments[0].studentName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {assessments[0].class} - {competition.olympiad}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Preparation Progress
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={competition.preparationProgress}
                                                    sx={{ width: 120, height: 8, borderRadius: 4 }}
                                                />
                                                <Typography variant="body2" fontWeight={600}>
                                                    {competition.preparationProgress}%
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Competition Stages Stepper */}
                                    <Stepper activeStep={competition.currentStage} alternativeLabel sx={{ mb: 3 }}>
                                        {competition.stages.map((stage, index) => (
                                            <Step key={index} completed={stage.status === 'completed' || stage.status === 'qualified'}>
                                                <StepLabel
                                                    error={stage.status === 'failed'}
                                                    StepIconProps={{
                                                        sx: {
                                                            color: stage.status === 'completed' || stage.status === 'qualified'
                                                                ? '#4caf50' : stage.status === 'in-progress' ? '#2196f3' : '#bdbdbd'
                                                        }
                                                    }}
                                                >
                                                    <Typography variant="caption" fontWeight={600}>
                                                        {stage.name}
                                                    </Typography>
                                                </StepLabel>
                                            </Step>
                                        ))}
                                    </Stepper>

                                    {/* Timeline Details */}
                                    <Timeline position="alternate">
                                        {competition.stages.map((stage, index) => (
                                            <TimelineItem key={index}>
                                                <TimelineOppositeContent color="text.secondary">
                                                    <Typography variant="body2">
                                                        {new Date(stage.date).toLocaleDateString()}
                                                    </Typography>
                                                </TimelineOppositeContent>
                                                <TimelineSeparator>
                                                    <TimelineDot color={getStageColor(stage.status)}>
                                                        {stage.status === 'completed' || stage.status === 'qualified' ? (
                                                            <CheckCircleIcon fontSize="small" />
                                                        ) : stage.status === 'in-progress' ? (
                                                            <TrendingUpIcon fontSize="small" />
                                                        ) : (
                                                            <FlagIcon fontSize="small" />
                                                        )}
                                                    </TimelineDot>
                                                    {index < competition.stages.length - 1 && <TimelineConnector />}
                                                </TimelineSeparator>
                                                <TimelineContent>
                                                    <Paper elevation={2} sx={{ p: 2 }}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {stage.name}
                                                        </Typography>
                                                        <Chip
                                                            label={stage.status}
                                                            color={getStageColor(stage.status)}
                                                            size="small"
                                                            sx={{ mt: 1 }}
                                                        />
                                                        {stage.score !== '-' && (
                                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                                Score: <strong>{stage.score}</strong>
                                                            </Typography>
                                                        )}
                                                    </Paper>
                                                </TimelineContent>
                                            </TimelineItem>
                                        ))}
                                    </Timeline>

                                    {/* Achievements */}
                                    {competition.achievements.length > 0 && (
                                        <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                                Achievements
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {competition.achievements.map((achievement, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        icon={<TrophyIcon />}
                                                        label={achievement}
                                                        color="warning"
                                                        size="small"
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
};

export default HolisticAssessment;
